import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { driverRecordShape } from './driverRecordShape';
import styledComponent from '../../../utils/styledComponent';
import theme from '../../../theme';
import DriverCard from './DriverCard';

const StyledDiv = styledComponent('div', {
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  minHeight: 0,

  '& .dcl-header': {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing.fieldGap,
    padding: '0 4px 12px',
  },
  '& .dcl-title': {
    fontSize: theme.sizes.headingFontSize,
    fontWeight: theme.fontWeights.semibold,
    color: theme.colors.textDark,
  },
  '& .dcl-subtitle': {
    marginTop: '3px',
    fontSize: theme.sizes.smallFontSize,
    color: theme.colors.textMuted,
  },

  '& .dcl-chips': {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '6px',
    padding: '0 4px 12px',
  },
  '& .dcl-chip': {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '4px 10px',
    fontSize: theme.sizes.smallFontSize,
    fontWeight: theme.fontWeights.medium,
    border: `1px solid ${theme.colors.borderLight}`,
    background: theme.colors.bgCard,
    color: theme.colors.textBody,
    borderRadius: theme.radii.pill,
    cursor: 'pointer',
    transition: theme.transitions.default,
  },
  '& .dcl-chip:hover': {
    background: theme.colors.bgMuted,
  },
  '& .dcl-chip.active': {
    background: theme.colors.textDark,
    color: theme.colors.white,
    borderColor: theme.colors.textDark,
  },
  '& .dcl-chip-count': {
    fontSize: theme.sizes.captionFontSize,
    color: 'inherit',
    opacity: 0.7,
    fontVariantNumeric: 'tabular-nums',
  },
  '& .dcl-chip-dot': {
    width: '7px',
    height: '7px',
    borderRadius: theme.radii.circle,
    flexShrink: 0,
  },
  '& .dcl-chip-dot-late': { background: theme.colors.statusOffTrackSoft },
  '& .dcl-chip-dot-on-track': { background: theme.colors.statusOnTrackSoft },
  '& .dcl-chip-dot-not-started': { background: theme.colors.statusPending },
  '& .dcl-chip-dot-completed': { background: theme.colors.statusCompleted },

  '& .dcl-list': {
    flex: 1,
    overflowY: 'auto',
    margin: '0 -8px',
  },

  '& .dcl-group-label': {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'baseline',
    padding: '12px 12px 6px',
    fontSize: theme.sizes.microFontSize,
    fontWeight: theme.fontWeights.semibold,
    color: theme.colors.textMuted,
    textTransform: 'uppercase',
    letterSpacing: '0.8px',
    background: theme.colors.bgMuted,
    borderBottom: `1px solid ${theme.colors.borderLight}`,
  },
  '& .dcl-group-count': {
    fontSize: theme.sizes.microFontSize,
    fontWeight: theme.fontWeights.normal,
    color: theme.colors.textHint,
    textTransform: 'none',
    letterSpacing: 0,
  },

  '& .dcl-empty': {
    padding: '40px 20px',
    textAlign: 'center',
    color: theme.colors.textMuted,
    fontSize: theme.sizes.bodyFontSize,
    fontStyle: 'italic',
  },
});

// Lifecycle filters only for now. The lateness-based chips (Late / On-track)
// are intentionally omitted until route-level timing data lands — driver shift
// windows + actual (Salesforce) delivery windows — since route-level lateness
// can't be computed reliably without them. Re-add the two once that data is
// wired up (the matchers are: Late → behind|off-track, On-track → on-track).
const STATUS_CHIPS = [
  { key: 'all', label: 'All', dot: null, matches: () => true },
  {
    key: 'not-started',
    label: 'Not started',
    dot: 'not-started',
    matches: (d) => d.status === 'not-started',
  },
  {
    key: 'completed',
    label: 'Completed',
    dot: 'completed',
    matches: (d) => d.status === 'completed',
  },
];

const GROUPS = [
  {
    key: 'active',
    label: 'Active',
    statuses: ['off-track', 'behind', 'on-track'],
    countWord: 'in progress',
  },
  {
    key: 'not-started',
    label: 'Not yet started',
    statuses: ['not-started'],
    countWord: 'awaiting dispatch',
  },
  {
    key: 'completed',
    label: 'Completed',
    statuses: ['completed'],
    countWord: 'delivered',
  },
];

// Sort active drivers by severity so off-track surfaces above behind, above on-track.
const ACTIVE_SEVERITY = { 'off-track': 0, behind: 1, 'on-track': 2 };

function DriverCardsList({
  drivers,
  isLoading,
  error,
  onSelectRoute,
  onSelectStop,
  onViewBreakdown,
}) {
  const [activeChip, setActiveChip] = useState('all');

  const chipCounts = useMemo(() => {
    const counts = {};
    STATUS_CHIPS.forEach((chip) => {
      counts[chip.key] = drivers.filter(chip.matches).length;
    });
    return counts;
  }, [drivers]);

  // Route and driver focus are applied upstream by MonitoringTab's filter bar;
  // the status chip is the only local filter.
  const filtered = useMemo(() => {
    const chip =
      STATUS_CHIPS.find((c) => c.key === activeChip) || STATUS_CHIPS[0];
    return drivers.filter(chip.matches);
  }, [drivers, activeChip]);

  const grouped = useMemo(
    () =>
      GROUPS.map((group) => {
        const records = filtered
          .filter((d) => group.statuses.includes(d.status))
          .slice()
          .sort((a, b) => {
            if (group.key === 'active') {
              const sevA = ACTIVE_SEVERITY[a.status] ?? 99;
              const sevB = ACTIVE_SEVERITY[b.status] ?? 99;
              if (sevA !== sevB) return sevA - sevB;
            }
            return (a.driverName || '').localeCompare(b.driverName || '');
          });
        return { ...group, records };
      }),
    [filtered],
  );

  const totalShown = filtered.length;
  const totalAvailable = drivers.length;
  const subtitle =
    totalShown === totalAvailable
      ? `Showing all ${totalAvailable} ${
          totalAvailable === 1 ? 'driver' : 'drivers'
        }`
      : `Showing ${totalShown} of ${totalAvailable}`;

  return (
    <StyledDiv>
      <div className="dcl-header">
        <div>
          <div className="dcl-title">Drivers</div>
          <div className="dcl-subtitle">
            {isLoading && totalAvailable === 0 ? 'Loading…' : subtitle}
          </div>
        </div>
      </div>

      <div
        className="dcl-chips"
        role="tablist"
        aria-label="Filter drivers by status"
      >
        {STATUS_CHIPS.map((chip) => (
          <button
            key={chip.key}
            type="button"
            role="tab"
            aria-selected={activeChip === chip.key}
            className={`dcl-chip ${activeChip === chip.key ? 'active' : ''}`}
            onClick={() => setActiveChip(chip.key)}
          >
            {chip.dot && (
              <span className={`dcl-chip-dot dcl-chip-dot-${chip.dot}`} />
            )}
            <span>{chip.label}</span>
            <span className="dcl-chip-count">{chipCounts[chip.key]}</span>
          </button>
        ))}
      </div>

      <div className="dcl-list">
        {error && (
          <div className="dcl-empty">
            Failed to load driver locations. Retrying on the next poll…
          </div>
        )}
        {!error && totalShown === 0 && totalAvailable === 0 && !isLoading && (
          <div className="dcl-empty">No drivers on the road right now.</div>
        )}
        {!error && totalShown === 0 && totalAvailable > 0 && (
          <div className="dcl-empty">No drivers match the current filter.</div>
        )}
        {grouped.map((group) => {
          if (group.records.length === 0) return null;
          return (
            <div key={group.key}>
              <div className="dcl-group-label">
                <span>{group.label}</span>
                <span className="dcl-group-count">
                  {`${group.records.length} ${
                    group.records.length === 1 ? 'route' : 'routes'
                  } ${group.countWord}`}
                </span>
              </div>
              {group.records.map((driver) => (
                <DriverCard
                  key={driver.routeId}
                  driver={driver}
                  onViewBreakdown={onViewBreakdown}
                  onSelectRoute={onSelectRoute}
                  onSelectStop={onSelectStop}
                />
              ))}
            </div>
          );
        })}
      </div>
    </StyledDiv>
  );
}

DriverCardsList.propTypes = {
  drivers: PropTypes.arrayOf(driverRecordShape).isRequired,
  isLoading: PropTypes.bool,
  // AppSync errors come back as `{ data, errors: [...] }`, not Error instances.
  error: PropTypes.oneOfType([PropTypes.instanceOf(Error), PropTypes.object]),
  onSelectRoute: PropTypes.func,
  onSelectStop: PropTypes.func,
  onViewBreakdown: PropTypes.func,
};

DriverCardsList.defaultProps = {
  isLoading: false,
  error: null,
  onSelectRoute: null,
  onSelectStop: null,
  onViewBreakdown: null,
};

export default DriverCardsList;
